import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import toast from "react-hot-toast";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import DataTable, { Column } from "@/components/DataTable";
import { LoadingState } from "@/components/LoadingState";
import SeverityBadge from "@/components/SeverityBadge";
import { alertApi } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import { useOrgId } from "@/hooks/useOrgId";
import type { Alert } from "@/types";

export default function AlertsPage() {
  const user = useAuthStore((s) => s.user)!;
  const orgId = useOrgId();
  const canResolve = user.is_staff || !!user.role?.can_manage_alerts;
  const [rows, setRows] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);

  const load = async () => {
    setLoading(true);
    setRows(await alertApi.forOrg(orgId, unresolvedOnly));
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, [orgId, unresolvedOnly]);

  const resolve = async (a: Alert) => {
    try {
      await alertApi.resolve(a.id);
      toast.success("Alert resolved");
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const columns: Column<Alert>[] = [
    { key: "msg", header: "Message", render: (a) => <span className="font-medium">{a.message}</span> },
    {
      key: "loc",
      header: "Location",
      render: (a) => (
        <span className="text-sm text-slate-600">
          {a.building_name ?? "—"} · {a.unit_number ?? "—"} · {a.meter_number ?? "—"}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (a) => <span className="badge bg-slate-100 text-slate-700">{a.alert_type.replace("_", " ")}</span>,
    },
    { key: "sev", header: "Severity", render: (a) => <SeverityBadge severity={a.severity} /> },
    {
      key: "when",
      header: "Created",
      render: (a) => new Date(a.created_at).toLocaleString("en-ZA"),
    },
    {
      key: "actions",
      header: "",
      render: (a) =>
        a.is_resolved ? (
          <span className="badge bg-brand-50 text-brand-800">Resolved</span>
        ) : canResolve ? (
          <button onClick={() => resolve(a)} className="btn-secondary text-xs" type="button">
            <CheckCircle2 size={14} /> Resolve
          </button>
        ) : null,
    },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Alerts"
        subtitle="Anomalies, missing readings, threshold breaches"
        actions={
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={unresolvedOnly}
              onChange={(e) => setUnresolvedOnly(e.target.checked)}
            />
            Unresolved only
          </label>
        }
      />
      {loading ? <LoadingState /> : <DataTable columns={columns} rows={rows} emptyMessage="No alerts" />}
    </AppLayout>
  );
}
